/**
 * DynamicConnectionForm
 *
 * Form component that dynamically renders connection parameters from a parameter set.
 * Supports multi-auth through parameter set selection.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Dropdown,
  Option,
  Text,
  Field,
  type SelectionEvents,
  type OptionOnSelectData,
} from '@fluentui/react-components';
import {
  type ConnectionParameterSet,
  type FormValues,
  type FormValidationErrors,
  type FormFieldValue,
  isParameterVisible,
  validateFormValues,
  getInitialFormValues,
  hasOAuthParameter,
} from '@/types/connectionParameters';
import { UniversalConnectionParameter } from './UniversalConnectionParameter';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  authTypeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalS,
  },
  authTypeLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  authTypeDescription: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },

  parametersSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },

  divider: {
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

export interface DynamicConnectionFormProps {
  /** Available parameter sets (different auth methods) */
  parameterSets: ConnectionParameterSet[];
  /** Currently selected parameter set name */
  selectedParameterSet?: string;
  /** Handler for parameter set change */
  onParameterSetChange?: (setName: string) => void;
  /** Current form values */
  values: FormValues;
  /** Handler for form value changes */
  onChange: (values: FormValues) => void;
  /** Validation errors */
  errors?: FormValidationErrors;
  /** Handler for OAuth button click */
  onOAuthClick?: () => void;
  /** Whether OAuth is in progress */
  isOAuthInProgress?: boolean;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Whether to show the auth type selector */
  showAuthTypeSelector?: boolean;
  /** Hide the displayName field (when managed externally) */
  hideDisplayName?: boolean;
  /** Parameter keys to exclude from rendering (handled externally) */
  excludeKeys?: string[];
}

/**
 * Dynamic form for connection parameters
 */
export const DynamicConnectionForm = ({
  parameterSets,
  selectedParameterSet,
  onParameterSetChange,
  values,
  onChange,
  errors = {},
  onOAuthClick,
  isOAuthInProgress = false,
  disabled = false,
  showAuthTypeSelector = true,
  hideDisplayName = false,
  excludeKeys = [],
}: DynamicConnectionFormProps) => {
  const styles = useStyles();

  // Get current parameter set
  const currentSet = useMemo(() => {
    if (!selectedParameterSet) {
      return parameterSets[0];
    }
    return parameterSets.find((s) => s.name === selectedParameterSet) ?? parameterSets[0];
  }, [parameterSets, selectedParameterSet]);

  // Get visible parameters (filter out displayName if hideDisplayName is true,
  // and always filter out OAuth/ManagedIdentity parameters since they are handled
  // at the panel level via the Sign In / Create button flow)
  const visibleParameters = useMemo(() => {
    if (!currentSet) return [];

    return Object.entries(currentSet.parameters).filter(([key, param]) => {
      // Hide displayName field if managed externally
      if (hideDisplayName && key === 'displayName') {
        return false;
      }
      // Hide explicitly excluded keys (handled externally)
      if (excludeKeys.includes(key)) {
        return false;
      }
      // OAuth and ManagedIdentity are handled by the panel, not as form fields
      if (param.type === 'oauthSetting' || param.type === 'managedIdentity') {
        return false;
      }
      return isParameterVisible(param, values);
    });
  }, [currentSet, values, hideDisplayName, excludeKeys]);

  // Handle parameter set selection
  const handleParameterSetChange = useCallback(
    (_e: SelectionEvents, data: OptionOnSelectData) => {
      if (data.optionValue && onParameterSetChange) {
        onParameterSetChange(data.optionValue);

        // Reset form values to defaults for new parameter set
        const newSet = parameterSets.find((s) => s.name === data.optionValue);
        if (newSet) {
          onChange(getInitialFormValues(newSet));
        }
      }
    },
    [onParameterSetChange, parameterSets, onChange]
  );

  // Handle field change
  const handleFieldChange = useCallback(
    (key: string, value: FormFieldValue) => {
      onChange({
        ...values,
        [key]: value,
      });
    },
    [values, onChange]
  );

  // Check if current set has OAuth
  const showOAuthButton = useMemo(
    () => currentSet && hasOAuthParameter(currentSet),
    [currentSet]
  );

  // Empty state
  if (!currentSet || parameterSets.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text>No connection parameters available</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Auth type selector (for multi-auth connectors) */}
      {showAuthTypeSelector && parameterSets.length > 1 && (
        <div className={styles.authTypeSection}>
          <Field label="Authentication Method">
            <Dropdown
              value={currentSet.uiDefinition?.displayName ?? currentSet.name}
              selectedOptions={[currentSet.name]}
              onOptionSelect={handleParameterSetChange}
              disabled={disabled}
              data-testid="auth-type-selector"
            >
              {parameterSets.map((set) => (
                <Option key={set.name} value={set.name}>
                  {set.uiDefinition?.displayName ?? set.name}
                </Option>
              ))}
            </Dropdown>
          </Field>
          {currentSet.uiDefinition?.description && (
            <Text className={styles.authTypeDescription}>
              {currentSet.uiDefinition.description}
            </Text>
          )}
        </div>
      )}

      {/* Parameters */}
      <div className={styles.parametersSection}>
        {visibleParameters.map(([key, param]) => {
          const fieldError = errors[key];
          const paramProps: {
            parameterKey: string;
            parameter: typeof param;
            value: FormFieldValue;
            onChange: typeof handleFieldChange;
            disabled: boolean;
            isOAuthInProgress: boolean;
            error?: typeof fieldError;
            onOAuthClick?: () => void;
          } = {
            parameterKey: key,
            parameter: param,
            value: values[key],
            onChange: handleFieldChange,
            disabled,
            isOAuthInProgress,
          };
          if (fieldError) {
            paramProps.error = fieldError;
          }
          if (showOAuthButton && onOAuthClick) {
            paramProps.onOAuthClick = onOAuthClick;
          }
          return <UniversalConnectionParameter key={key} {...paramProps} />;
        })}
      </div>
    </div>
  );
};

DynamicConnectionForm.displayName = 'DynamicConnectionForm';

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook for managing dynamic connection form state
 */
export interface UseDynamicConnectionFormOptions {
  /** Available parameter sets */
  parameterSets: ConnectionParameterSet[];
  /** Initial parameter set name */
  initialParameterSet?: string;
  /** Initial form values */
  initialValues?: FormValues;
}

export interface UseDynamicConnectionFormReturn {
  /** Current parameter set */
  currentSet: ConnectionParameterSet | undefined;
  /** Selected parameter set name */
  selectedParameterSet: string;
  /** Form values */
  values: FormValues;
  /** Validation errors */
  errors: FormValidationErrors;
  /** Whether form is valid */
  isValid: boolean;
  /** Whether form is dirty */
  isDirty: boolean;
  /** Set parameter set */
  setParameterSet: (name: string) => void;
  /** Set field value */
  setFieldValue: (key: string, value: FormFieldValue) => void;
  /** Set all values */
  setValues: (values: FormValues) => void;
  /** Validate form */
  validate: () => FormValidationErrors;
  /** Reset form */
  reset: () => void;
}

export function useDynamicConnectionForm({
  parameterSets,
  initialParameterSet,
  initialValues,
}: UseDynamicConnectionFormOptions): UseDynamicConnectionFormReturn {
  const [selectedParameterSet, setSelectedParameterSet] = useState(
    initialParameterSet ?? parameterSets[0]?.name ?? ''
  );

  const currentSet = useMemo(
    () => parameterSets.find((s) => s.name === selectedParameterSet),
    [parameterSets, selectedParameterSet]
  );

  const [values, setValues] = useState<FormValues>(
    initialValues ?? (currentSet ? getInitialFormValues(currentSet) : {})
  );

  const [errors, setErrors] = useState<FormValidationErrors>({});
  const [isDirty, setIsDirty] = useState(false);

  // Reset values when parameter set changes
  useEffect(() => {
    if (currentSet) {
      setValues(getInitialFormValues(currentSet));
      setErrors({});
      setIsDirty(false);
    }
  }, [currentSet]);

  const setFieldValue = useCallback((key: string, value: FormFieldValue) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
    // Clear error for this field
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    if (!currentSet) return {};
    const validationErrors = validateFormValues(values, currentSet);
    setErrors(validationErrors);
    return validationErrors;
  }, [currentSet, values]);

  const isValid = useMemo(() => {
    if (!currentSet) return false;
    return Object.keys(validateFormValues(values, currentSet)).length === 0;
  }, [currentSet, values]);

  const reset = useCallback(() => {
    if (currentSet) {
      setValues(getInitialFormValues(currentSet));
      setErrors({});
      setIsDirty(false);
    }
  }, [currentSet]);

  return {
    currentSet,
    selectedParameterSet,
    values,
    errors,
    isValid,
    isDirty,
    setParameterSet: setSelectedParameterSet,
    setFieldValue,
    setValues,
    validate,
    reset,
  };
}
